import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  IconButton,
  Grid,
  Box,
  Typography,
  Drawer,
  Paper,
  Stack,
  Toolbar,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { ArrowBack } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DatasetField from './DatasetField';
import { fetchDatasets } from '../../../actions/datasets';
import { fetchPublicDatasets } from '../../../actions/publicDatasets';

export default function DatasetDrawer({
  open,
  handleClose,
  initialValues,
  setValue,
}) {
  const dispatch = useDispatch();
  const user = useUser();
  const datasets = useSelector((state) => state.datasets);
  const publicDatasets = useSelector((state) => state.publicDatasets);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const safe_initial_values = Array.isArray(initialValues) ? initialValues : [];

  const [selectedDatasets, setSelectedDatasets] = useState(
    safe_initial_values.map((dataset) => ({ ...dataset })),
  );

  const [dataset_errors, setDatasetErrors] = useState(
    new Array(safe_initial_values.length).fill(false),
  );

  const selectedDatasetsRef = useRef(
    safe_initial_values.map((dataset) => ({ ...dataset })),
  );

  useEffect(() => {
    selectedDatasetsRef.current = selectedDatasets;
  }, [selectedDatasets]);

  useEffect(() => {
    const safe = Array.isArray(initialValues) ? initialValues : [];
    const new_datasets = safe.map((dataset) => ({ ...dataset }));
    selectedDatasetsRef.current = new_datasets;
    setSelectedDatasets(new_datasets);
  }, [initialValues]);

  useEffect(() => {
    (async () => {
      if (datasets === null) {
        if (user?.selected_project) {
          dispatch(fetchDatasets(user.selected_project));
        }
        return;
      }
    })();
  }, [dispatch, datasets, user?.selected_project]);

  useEffect(() => {
    (async () => {
      if (publicDatasets === null) {
        dispatch(fetchPublicDatasets());
        return;
      }
    })();
  }, [dispatch, publicDatasets]);

  useEffect(() => {
    setLoading(datasets === null || publicDatasets === null);
  }, [datasets, publicDatasets]);

  const datasetErrorCheck = useCallback((datasets) => {
    const names = datasets.map((dataset) => {
      if (dataset.name) {
        return dataset.name.toLowerCase().replace(' ', '_');
      }
      return undefined;
    });
    const dup_names = names.filter((e, i, a) => a.indexOf(e) !== i);
    const errors = datasets.map((dataset) => {
      if (dataset.name) {
        return dup_names.includes(dataset.name.toLowerCase().replace(' ', '_'));
      }
      return true;
    });
    return errors;
  }, []);

  const calculateError = useCallback(
    (dataset_errors) => {
      const errors_set = new Set([...dataset_errors]);
      if (
        errors_set.size > 1 ||
        ([...errors_set][0] && [...errors_set][0] !== false)
      ) {
        setError(true);
      } else {
        setError(false);
      }
    },
    [setError],
  );

  const setDatasetError = useCallback(
    (i, error) => {
      setDatasetErrors((prevErrors) => {
        const new_errors = [...prevErrors];
        new_errors[i] = error;

        // Recalculate duplicates using current selectedDatasets from ref
        const duplicateErrors = datasetErrorCheck(selectedDatasetsRef.current);
        const combinedErrors = new_errors.map((err, idx) => {
          // If there's an explicit error (string), keep it, otherwise use duplicate check
          return err && err !== false ? err : duplicateErrors[idx];
        });
        calculateError(combinedErrors);
        return combinedErrors;
      });
    },
    [calculateError, datasetErrorCheck],
  );

  const addDataset = useCallback(() => {
    const new_datasets = [
      ...selectedDatasets,
      { type: 'existing', dataset_uuid: '' },
    ];
    selectedDatasetsRef.current = new_datasets;
    setSelectedDatasets(new_datasets);
    const new_errors = [...dataset_errors, 'Dataset cannot be blank'];
    setDatasetErrors(new_errors);
    calculateError(new_errors);
  }, [
    selectedDatasets,
    dataset_errors,
    setSelectedDatasets,
    calculateError,
    setDatasetErrors,
  ]);

  const removeDataset = useCallback(
    (i) => {
      const new_datasets = [...selectedDatasets];
      new_datasets.splice(i, 1);
      selectedDatasetsRef.current = new_datasets;

      setSelectedDatasets(new_datasets);
      const new_errors = [...dataset_errors];
      new_errors.splice(i, 1);

      // Recheck for duplicates after removal
      const duplicateErrors = datasetErrorCheck(new_datasets);
      const combinedErrors = new_errors.map((err, idx) => {
        return err && err !== false ? err : duplicateErrors[idx];
      });

      setDatasetErrors(combinedErrors);
      calculateError(combinedErrors);
    },
    [
      selectedDatasets,
      dataset_errors,
      setSelectedDatasets,
      setDatasetErrors,
      calculateError,
      datasetErrorCheck,
    ],
  );

  const setDatasetValue = useCallback(
    (i, values) => {
      setSelectedDatasets((prevDatasets) => {
        const new_datasets = [...prevDatasets];
        new_datasets[i] = values;
        selectedDatasetsRef.current = new_datasets;

        // Recheck for duplicates after value change
        setDatasetErrors((prevErrors) => {
          const duplicateErrors = datasetErrorCheck(new_datasets);
          const currentErrors = [...prevErrors];
          // Only update error if it's not already an explicit error (string)
          currentErrors[i] =
            currentErrors[i] && currentErrors[i] !== false
              ? currentErrors[i]
              : duplicateErrors[i];
          calculateError(currentErrors);
          return currentErrors;
        });

        return new_datasets;
      });
    },
    [datasetErrorCheck, calculateError],
  );

  const submit = useCallback(() => {
    // Enrich datasets with name and size from Redux store
    const allDatasets = [...(datasets || []), ...(publicDatasets || [])];
    const enrichedDatasets = selectedDatasets.map((dataset) => {
      if (!dataset.dataset_uuid) {
        return dataset;
      }
      const fullDataset = allDatasets.find(
        (d) => d.dataset_uuid === dataset.dataset_uuid
      );
      return {
        ...dataset,
        name: fullDataset ? fullDataset.name : dataset.name || '',
        size: fullDataset ? fullDataset.used_size || fullDataset.size : dataset.size,
      };
    });
    setValue(enrichedDatasets);
    handleClose();
  }, [selectedDatasets, setValue, handleClose, datasets, publicDatasets]);

  return (
    <Drawer
      sx={{ height: '100%' }}
      anchor='right'
      open={open}
      onClose={handleClose}
    >
      <Grid
        container
        spacing={2}
        direction='row'
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          margin: '0px',
          padding: '0px 0px 0px 0px !important',
          '.MuiGrid-root': {
            padding: '0px 0px 0px 0px',
          },
          maxWidth: '550px',
        }}
      >
        <Grid
          container
          justifyContent='flex-start'
          sx={{
            maxHeight: '75px',
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
            }}
            elevation={1}
          >
            <Toolbar>
              <Stack spacing={2} direction='row' alignItems='center'>
                <IconButton onClick={() => handleClose()}>
                  <ArrowBack />
                </IconButton>
                <Typography variant='h5'>Manage Datasets</Typography>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
        <Grid
          size={{ xs: 12, md: 12, lg: 12 }}
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
          }}
        >
          <Backdrop open={loading}>
            <CircularProgress />
          </Backdrop>
          <Box sx={{ padding: '16px' }}>
            <Stack spacing={2} direction='column' alignItems='center'>
              {selectedDatasets.map((value, index) => {
                return (
                  <DatasetField
                    key={`dataset-${index}`}
                    index={index}
                    value={value}
                    error={dataset_errors[index]}
                    setError={setDatasetError}
                    setValue={setDatasetValue}
                    remove={removeDataset}
                  />
                );
              })}
              <Button
                variant='outlined'
                onClick={() => addDataset()}
                color='brand'
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <AddCircleOutlineIcon />
                  <Typography variant='button' display='block' gutterBottom>
                    Add Dataset
                  </Typography>
                </Stack>
              </Button>
            </Stack>
          </Box>
        </Grid>
        <Grid
          container
          sx={{
            maxHeight: '50px',
            position: 'absolute',
            bottom: '0px',
            width: '100%',
          }}
        >
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='contained'
                  onClick={submit}
                  disabled={error}
                  color='brand'
                >
                  Save
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    const safe = Array.isArray(initialValues) ? initialValues : [];
                    setSelectedDatasets(safe.map((dataset) => ({ ...dataset })));
                    setDatasetErrors(new Array(safe.length).fill(false));
                    setError(false);
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
      </Grid>
    </Drawer>
  );
}
